'use client';

/**
 * "Drive with" dialog — opened from an app's Drive pill (next to Remix).
 *
 * Remix hands an AI the app's FILES; Drive hands it the app's CONTROLS. The
 * app stays exactly where it is (no copy) — the chosen tool connects the
 * Robutler MCP and drives the LIVE instance on this canvas over the command
 * bus (workspace_widgets_list / invoke / logs), while the user watches the
 * agent's cursor, highlights and status through the in-app presence layer.
 *
 *  • Robutler agent  → drops an agent on the canvas prefilled to drive this app.
 *  • Claude Code / Codex / Antigravity / Cursor → fires that tool's deep-link
 *    with a ready drive prompt (connect the MCP, discover commands, call the
 *    app's `guide` first). Copy-the-prompt fallback covers uninstalled tools.
 *
 * Environment roster + deep-link schemes come from coding-env-marks.tsx (the
 * single source shared with the Remix and New-app dialogs).
 */

import { createPortal } from 'react-dom';
import { useT } from '@/components/i18n-provider';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Copy, ExternalLink, Search, X } from 'lucide-react';
import { CODING_ENVS, launchCodingEnv, type CodingEnv } from './coding-env-marks';

/** An agent chosen in the picker (the default assistant when omitted). */
export interface DriveAgent {
  id: string;
  username: string;
  displayName?: string | null;
  avatarUrl?: string | null;
}

export interface DriveTarget {
  /** The app's display name (drive prompt copy). */
  widgetName: string;
  /**
   * The LIVE instance the tool will drive (canvas mounts, or a fullscreen
   * view launched from one — commands route to any live tab of the
   * workspace, and the canvas tab stays open behind a maximize).
   */
  workspaceId?: string;
  itemId?: string;
  /**
   * Standalone `/w/` view: the shared content id. The share view is
   * itself a LIVE command surface (it registers presence under
   * `share-<contentId>` and appears in `workspace_list_open` as
   * kind:"share"), so the prompt routes commands to THIS tab —
   * `widget_instantiate` is only the fallback if the tab is closed.
   */
  contentId?: string;
  /**
   * The app's wire type (`revent`, `crm`). What the person's own agent FOR
   * THIS APP is minted and named from: `@<username>.<widgetType>`. Omitted
   * where the caller does not know it, and the dialog then falls back to the
   * shared default assistant.
   */
  widgetType?: string;
  /**
   * The app row running in the window (the item's own row, or the open-with
   * handler's). Sent with the mint, because the server mints the person's
   * agent for an app only from the app's own catalog row, never from a copy
   * that kept its type (S-173). Without it the mint is refused and the row
   * below falls back to the shared assistant.
   */
  appContentId?: string;
  /**
   * A task the APP proposed (host.ui.driveWith). Appended to the drive prompt
   * so the agent starts on what the person was actually stuck on — "help me
   * finish connecting email" rather than a generic tour of the commands. The
   * person still reads it here and still presses go.
   */
  task?: string | null;
}

interface DriveWithDialogProps {
  open: boolean;
  onClose: () => void;
  target: DriveTarget;
  /** Drop a Robutler agent on the canvas prefilled to drive this app.
   *  Called with the PICKED agent (undefined = the default assistant).
   *  Omit where there is no canvas (standalone view) — the row is hidden.
   *
   *  Answer `false` when the agent could not be put on the canvas and this
   *  dialog stays open and says so. It used to close unconditionally and the
   *  failure was a `console.warn`: on 2026-09-16 a loaded server timed out the
   *  mount three times running, the person was left looking at a canvas with
   *  no new agent on it and no message, and typed their request into whatever
   *  chat happened to be underneath. */
  onRobutler?: (agent?: DriveAgent) => void | boolean | Promise<void | boolean>;
}

const ENV_SUBS: Record<CodingEnv['id'], string> = {
  robutler: 'Drive it here with a Robutler agent',
  claude: 'Opens a Code session in Claude Desktop',
  codex: 'Opens a new Codex thread',
  antigravity: 'Opens Google Antigravity',
  cursor: 'Opens Cursor with the prompt',
};

function buildDrivePrompt(t: DriveTarget): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://robutler.ai';
  const canvasLive = !!(t.workspaceId && t.itemId);
  // Both surfaces are LIVE targets: a canvas mount routes by workspace +
  // item id; a standalone /w/ view routes by its contentId (the share
  // surface registers presence like a canvas tab does). The prompt must
  // describe ONE surface truthfully — never "mount it first" while the
  // app is already open in front of the user.
  const step2 = canvasLive
    ? `2) The app is open in my browser: workspace ${t.workspaceId}, item ${t.itemId}. Commands route to my live tab.`
    : `2) The app is open in my browser as a share view (contentId ${t.contentId}). It shows up in workspace_list_open as kind:"share" — pass that contentId as itemId to workspace_widgets_invoke and commands route to my live tab. Only if no share tab is listed (I may have closed it): ask me to reopen it, or mount a copy with widget_instantiate refId ${t.contentId} and I will open the canvas.`;
  return [
    `Drive my Robutler app "${t.widgetName}" — it is LIVE in my browser, no copy needed.`,
    '',
    // "Connect the server" was the wrong first step: the connector is
    // usually already there (often under an opaque name), several Robutler
    // connectors can be live at once (prod, dev boxes), and a headless
    // session cannot run OAuth at all. Verify-first names the real move.
    `1) Use your Robutler MCP connection for ${origin}. Verify before driving: workspace_list_open must list the surface below - with several Robutler connectors connected, the right one is the one that lists it. Only if none answers: add ${origin}/mcp (Streamable HTTP, OAuth) and authorize it, or ask me to - OAuth cannot run in a non-interactive session.`,
    step2,
    `3) Discover its controls with workspace_widgets_list. If it declares a guide command, call it FIRST via workspace_widgets_invoke — that is the app's playbook (workflows, conventions, metadata contracts). Read state with its getters before editing; __screenshot shows you the app, workspace_widgets_logs its console.`,
    `4) I can watch you work — your cursor, target highlights and status show up live inside the app.`,
    '',
    // An app-proposed task replaces the generic opener: the person clicked
    // "ask for help" from a specific screen, so starting with "ask me what I
    // want" would throw away the one thing we already know.
    t.task
      ? `The person asked for help with this: ${t.task}`
      : `Ask me what I want to do, then drive the app with workspace_widgets_invoke.`,
  ].join('\n');
}

/**
 * What the Robutler hand-off puts in the PERSON'S composer: their words, and
 * only theirs (F4, the round 8 dev run, 2026-09-28). This used to seed the
 * composer with `Drive my app "Revent" on this canvas (item <uuid>). Discover
 * its commands with workspace_widgets_list, call its guide command first if it
 * has one, then help me use it. I'd like to:`, an agent instruction with an
 * item id and a tool name in it, shown as something the person is about to
 * say. The agent-facing part rides on channels that already reach the run and
 * never the composer: the canvas chat stamps the workspace and tab on every
 * message (chat-view's `x-workspace-tab-id`), so the agent's own
 * `workspace_widgets_list` finds the app and its commands on that tab; the
 * agent-control grant `doDriveWithRobutler` writes on the app item is the one
 * item the agent may drive; and the person's app agent is minted with the
 * standing instruction to list the app's commands and call `guide` first
 * (`appAgentInstructions`). The app-proposed task (`host.ui.driveWith`) is the
 * one thing the person actually asked for, so it is the sentence when there is
 * one. Both strings are i18n keys, as every other person-facing line here.
 */
export function robutlerDrivePrefill(
  t: (key: string, params?: Record<string, string | number>) => string,
  target: Pick<DriveTarget, 'widgetName' | 'task'>,
): string {
  const app = target.widgetName;
  const task = target.task?.trim();
  if (task) return t('workspace.driveWith.robutlerPrefillTask', { app, task });
  /* A trailing space: the composer seeds and focuses the textarea and parks
     the caret at the end, so the person's own words start after the colon. */
  return `${t('workspace.driveWith.robutlerPrefill', { app })} `;
}

/** Tolerant `{ agents: [...] } | { items: [...] } | [...]` → array. */
function agentRows(v: unknown): DriveAgent[] {
  const o = v as Record<string, unknown> | null | undefined;
  if (!o) return [];
  const arr = Array.isArray(o) ? o : Array.isArray(o.agents) ? o.agents : Array.isArray(o.items) ? o.items : [];
  return (arr as DriveAgent[]).filter((a) => a && typeof a.id === 'string' && typeof a.username === 'string');
}

/**
 * Ceiling on one type-ahead search (both fetches share it). 2026-09-06, two
 * browser runs on the local cluster under load: /api/discovery/agents (the
 * semantic path: on-CPU embed, Milvus hybrids, rerank per keystroke) sat
 * pending for minutes with no deadline, so the picker showed "Searching…"
 * forever and the person could not find their OWN agent. 8s is long enough
 * for a cold semantic query on a loaded box and short enough that a person
 * still gets an answer instead of concluding the dialog is broken; typing
 * again restarts the clock anyway.
 */
const SEARCH_DEADLINE_MS = 8000;

/* How long the app-agent mint may take before the row falls back to the
   shared assistant. Longer than the search deadline because this one WRITES
   (a users row plus a config row) and a cold route compile on a loaded dev
   server measured 23.4s of a 24.5s call; shorter than a person's patience
   with a control they cannot press. */
const MINT_DEADLINE_MS = 30000;

/**
 * How a finished search ended, per source. `failed` covers a network error,
 * a non-2xx, an unparseable body and the deadline; an empty match is NOT a
 * failure. Before 2026-09-06 both fetches swallowed errors into null, so a
 * search that could not run rendered exactly like "No agents match".
 */
interface SearchOutcome { mine: 'ok' | 'failed'; web: 'ok' | 'failed'; timedOut: boolean }

/** Null when both sources answered; otherwise what to tell the person. */
function searchFailureCopy(o: SearchOutcome): string | null {
  const mine = o.mine === 'failed';
  const web = o.web === 'failed';
  if (!mine && !web) return null;
  if (mine && web) return o.timedOut ? 'Search timed out.' : 'Search is not available right now.';
  if (web) return o.timedOut ? 'The web of agents took too long to answer.' : 'The web of agents could not be searched.';
  return o.timedOut ? 'Your agents took too long to load.' : 'Your agents could not be searched.';
}

export function DriveWithDialog({ open, onClose, target, onRobutler }: DriveWithDialogProps) {
  const t = useT();
  const [panel, setPanel] = useState<{ env: CodingEnv; prompt: string; url: string } | null>(null);
  const [copied, setCopied] = useState(false);
  // Robutler path: an AGENT PICKER (like the Agents panel) — the user's own
  // agents up front, typing searches the whole web of agents.
  const [agentPick, setAgentPick] = useState(false);
  const [q, setQ] = useState('');
  const [mine, setMine] = useState<DriveAgent[]>([]);
  const [mineTotal, setMineTotal] = useState<number | null>(null);
  const [mineHits, setMineHits] = useState<DriveAgent[]>([]);
  const [found, setFound] = useState<DriveAgent[]>([]);
  const [searching, setSearching] = useState(false);
  // How the last search ENDED, per source, so the empty state can tell
  // "nothing matched" from "could not search". Null while idle or in flight.
  const [outcome, setOutcome] = useState<SearchOutcome | null>(null);
  // Bumped by "Try again": re-runs the search effect for the same query.
  const [attempt, setAttempt] = useState(0);
  const loadingMoreRef = useRef(false);
  /* THE PERSON'S OWN AGENT FOR THIS APP (2026-09-16). The top row used to be
     a shared platform agent, which meant one exhausted usage allowance
     blocked everybody, and picking anything else meant answering "which of my
     agents should read my calendar" on day one. This mints `@you.<app>`
     instead, once, on the person's own account, and reuses it forever. It is
     resolved while the picker opens rather than on click, so the row can name
     the agent it is about to hand over. Null means the mint has not answered
     (or could not), and the row falls back to the shared default, which is
     still correct, just shared. */
  const [appAgent, setAppAgent] = useState<DriveAgent | null>(null);
  /* Whether the mint is still in flight. It has to be tracked separately from
     `appAgent`, because the two states it distinguishes look identical
     otherwise: "no agent yet, still asking" and "asked, cannot have one".
     (2026-09-16 browser pass: the mint took 40 seconds on a loaded dev server
     and the top row read "Robutler / The default assistant" the whole time,
     with no loading state. Anyone who clicked in that window got the shared
     agent, silently, which is precisely the thing this feature exists to stop.) */
  const [minting, setMinting] = useState(false);
  /* The mount is in flight, and whether the last one failed. */
  const [spawning, setSpawning] = useState(false);
  const [spawnFailed, setSpawnFailed] = useState(false);

  useEffect(() => {
    if (open) { setPanel(null); setCopied(false); setAgentPick(false); setQ(''); setFound([]); setMineTotal(null); setSpawnFailed(false); setSpawning(false); }
  }, [open]);

  // Minted (or found) as the picker opens. Failure is silent on purpose: the
  // row below falls back to the shared assistant, which is a working feature
  // rather than an error to read.
  useEffect(() => {
    if (!agentPick || !target.widgetType) return;
    let dead = false;
    setMinting(true);
    /* A DEADLINE, BECAUSE THE PENDING ROW IS UNCLICKABLE. Without one, a mint
       that never answers leaves "Getting your Revent agent / One moment."
       disabled forever and the person cannot pick anything at all: the
       loading state that stops them choosing the shared agent by accident
       becomes a dead end. Measured on a loaded dev server, this call took
       24.5 seconds, 23.4 of it a cold route compile. Falling back is safe and
       honest: the row becomes the shared assistant, which is what it was
       before this feature, and it is clickable again. */
    const ctrl = new AbortController();
    const deadline = setTimeout(() => ctrl.abort(), MINT_DEADLINE_MS);
    fetch('/api/widgets/agent', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ widgetType: target.widgetType, contentId: target.appContentId ?? null }),
      signal: ctrl.signal,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((v) => {
        const a = v as { agentId?: string; username?: string; displayName?: string } | null;
        if (!dead && a && typeof a.agentId === 'string' && typeof a.username === 'string') {
          setAppAgent({ id: a.agentId, username: a.username, displayName: a.displayName ?? null });
        }
      })
      .catch(() => {})
      .finally(() => { clearTimeout(deadline); if (!dead) setMinting(false); });
    return () => { dead = true; clearTimeout(deadline); ctrl.abort(); };
  }, [agentPick, target.widgetType, target.appContentId]);

  // My agents load once when the picker opens (first page); scrolling the list
  // pages in the rest. The search effect below narrows them (mineHits) and
  // adds web-of-agents hits as the user types.
  useEffect(() => {
    if (!agentPick) return;
    let dead = false;
    fetch('/api/users/me/agents?limit=24', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((v) => { if (!dead) { setMine(agentRows(v)); setMineTotal(typeof (v as { total?: number })?.total === 'number' ? (v as { total: number }).total : null); } })
      .catch(() => { if (!dead) setMine([]); });
    return () => { dead = true; };
  }, [agentPick]);

  // Infinite scroll for "Your agents" (browse mode only — search is capped).
  const loadMoreMine = useCallback(() => {
    if (q.trim() || loadingMoreRef.current) return;
    if (mineTotal != null && mine.length >= mineTotal) return;
    loadingMoreRef.current = true;
    fetch(`/api/users/me/agents?limit=24&offset=${mine.length}`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((v) => {
        const rows = agentRows(v);
        if (typeof (v as { total?: number })?.total === 'number') setMineTotal((v as { total: number }).total);
        if (rows.length) setMine((cur) => { const seen = new Set(cur.map((a) => a.id)); return [...cur, ...rows.filter((a) => !seen.has(a.id))]; });
        else setMineTotal((t) => t ?? mine.length); // empty page = the end
      })
      .catch(() => {})
      .finally(() => { loadingMoreRef.current = false; });
  }, [q, mine.length, mineTotal]);

  // Type-ahead search. Every exit path must leave `searching` truthful. The
  // 2026-09-06 hang: setSearching(true) ran synchronously on every keystroke
  // while the only setSearching(false) lived in the debounced callback,
  // behind two fetches with no deadline; a slow server pinned the flag for
  // good. Now each source applies its rows as it lands (your own agents are
  // a cheap DB read, the web of agents is the slow semantic path), one
  // deadline aborts whatever is still pending, and a superseded run (`stale`)
  // never touches state, so the microtask abort of the OLD fetches cannot
  // clear the flag the NEW run just raised.
  useEffect(() => {
    if (!agentPick || !q.trim()) { setMineHits([]); setFound([]); setOutcome(null); setSearching(false); return; }
    const enc = encodeURIComponent(q.trim());
    const ctrl = new AbortController();
    let stale = false;
    let timedOut = false;
    let deadline: ReturnType<typeof setTimeout> | undefined;
    setSearching(true);
    setOutcome(null);
    // `null` = this source FAILED (network, non-2xx, bad body, aborted). An
    // empty match is `[]`, never null, so the empty state can tell them apart.
    const load = async (url: string): Promise<DriveAgent[] | null> => {
      try {
        const r = await fetch(url, { credentials: 'same-origin', signal: ctrl.signal });
        return r.ok ? agentRows(await r.json()) : null;
      } catch { return null; }
    };
    const debounce = setTimeout(() => {
      deadline = setTimeout(() => { timedOut = true; ctrl.abort(); }, SEARCH_DEADLINE_MS);
      const mine = load(`/api/users/me/agents?search=${enc}&limit=8`).then((rows) => { if (!stale) setMineHits(rows ?? []); return rows; });
      const web = load(`/api/discovery/agents?search=${enc}&limit=8`).then((rows) => { if (!stale) setFound(rows ?? []); return rows; });
      void Promise.all([mine, web]).then(([m, w]) => {
        clearTimeout(deadline);
        if (stale) return;
        setOutcome({ mine: m ? 'ok' : 'failed', web: w ? 'ok' : 'failed', timedOut });
        setSearching(false);
      });
    }, 220);
    return () => {
      stale = true;
      clearTimeout(debounce);
      clearTimeout(deadline);
      ctrl.abort();
      // Torn down before the debounce fired: the callback that would have
      // cleared the flag never runs, so clear it here. A follow-up run raises
      // it again in the same commit, so nothing flickers.
      setSearching(false);
    };
  }, [agentPick, q, attempt]);

  /** Fire the tool's deep link and copy the prompt, both inside the click that
   *  called this. Deep link first: see `launchCodingEnv` for the Safari
   *  failure the old copy-then-open order produced. */
  const launch = useCallback((env: CodingEnv, prompt: string) => {
    const launched = launchCodingEnv(env, prompt);
    if (!launched) return null;
    void launched.copied.then((ok) => { if (ok) setCopied(true); });
    return launched;
  }, []);

  const chooseAgent = useCallback(async (agent?: DriveAgent) => {
    setSpawnFailed(false);
    setSpawning(true);
    let ok: void | boolean = true;
    try { ok = await onRobutler?.(agent); } catch { ok = false; }
    setSpawning(false);
    /* `undefined` is a caller that does not report, and those are the callers
       that worked before this returned anything: only an explicit false keeps
       the dialog open. */
    if (ok === false) { setSpawnFailed(true); return; }
    onClose();
  }, [onClose, onRobutler]);

  // Synchronous on purpose: nothing may run between the click and the deep
  // link (`launchCodingEnv`). The clipboard makes every outcome recoverable
  // with a single paste: an uninstalled tool opens nothing, and not every
  // scheme carries the prompt.
  const pick = useCallback((env: CodingEnv) => {
    if (env.id === 'robutler') { setAgentPick(true); return; }
    const prompt = buildDrivePrompt(target);
    const launched = launch(env, prompt);
    if (launched) setPanel({ env, prompt, url: launched.url });
  }, [launch, target]);

  const copyPrompt = useCallback(async () => {
    if (!panel) return;
    try { await navigator.clipboard.writeText(panel.prompt); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  }, [panel]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-sm overflow-hidden rounded-2xl border border-border bg-background shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="text-sm font-semibold">{agentPick ? 'Pick an agent to drive it' : `Drive with${panel ? ` ${panel.env.name}` : ''}`}</h2>
          <button
            onClick={onClose}
            aria-label={t('workspace.canvas.node.close')}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-muted-foreground hover:bg-accent"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {agentPick ? (
          (() => {
            /* The app agent is already the row above, so it is not listed
               again under "Your agents": one object, one place to tap. */
            const shown = q.trim() ? mineHits : mine;
            const myList = appAgent ? shown.filter((a) => a.id !== appAgent.id) : shown;
            const mineIds = new Set(myList.map((a) => a.id));
            const web = found.filter((a) => !mineIds.has(a.id));
            const failure = outcome ? searchFailureCopy(outcome) : null;
            const robutlerEnv = CODING_ENVS.find((e) => e.id === 'robutler');
            const row = (a: DriveAgent) => (
              <button
                key={a.id}
                onClick={() => { void chooseAgent(a); }}
                disabled={spawning}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors hover:bg-accent disabled:opacity-60"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-violet-100 text-xs font-semibold text-violet-700 dark:bg-violet-500/20 dark:text-violet-300">
                  {a.avatarUrl
                    // eslint-disable-next-line @next/next/no-img-element
                    ? <img src={a.avatarUrl} alt="" className="h-full w-full object-cover" />
                    : (a.displayName || a.username).slice(0, 1).toUpperCase()}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{a.displayName || a.username}</span>
                  <span className="block truncate text-xs text-muted-foreground">@{a.username}</span>
                </span>
              </button>
            );
            return (
              <div className="p-2">
                <div className="mx-2 mb-1.5 mt-1 flex items-center gap-2 rounded-lg bg-muted/50 px-2.5 py-1.5 ring-1 ring-border">
                  <Search className="h-3.5 w-3.5 opacity-50" />
                  <input
                    autoFocus
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={t('workspace.driveWith.searchAgents')}
                    className="w-full bg-transparent text-sm outline-none"
                  />
                </div>
                {/* FIXED height (viewport-capped): the dialog must not jump as
                    result counts change or while a search is in flight. */}
                <div
                  className="h-[min(19rem,50vh)] overflow-y-auto"
                  onScroll={(e) => {
                    const el = e.currentTarget;
                    if (el.scrollTop + el.clientHeight > el.scrollHeight - 120) loadMoreMine();
                  }}
                >
                  {!q.trim() ? (
                    <button
                      onClick={() => { if (!minting && !spawning) void chooseAgent(appAgent ?? undefined); }}
                      disabled={minting || spawning}
                      aria-busy={minting || spawning || undefined}
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors hover:bg-accent disabled:cursor-progress disabled:opacity-60 disabled:hover:bg-transparent"
                    >
                      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${robutlerEnv?.chip ?? 'bg-muted'}`}>
                        {robutlerEnv?.logo}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium">
                          {minting ? `Getting your ${target.widgetName} agent` : appAgent ? `Your ${target.widgetName} agent` : 'Robutler'}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {minting ? 'One moment.' : appAgent ? `@${appAgent.username}` : t('workspace.driveWith.defaultAssistant')}
                        </span>
                      </span>
                    </button>
                  ) : null}
                  {myList.length ? (
                    <p className="px-3 pb-0.5 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Your agents</p>
                  ) : null}
                  {myList.map(row)}
                  {web.length ? (
                    <p className="px-3 pb-0.5 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Web of agents</p>
                  ) : null}
                  {web.map(row)}
                  {q.trim() && !searching && outcome && !failure && !myList.length && !web.length ? (
                    <p className="px-3 py-4 text-center text-xs text-muted-foreground">No agents match “{q.trim()}”.</p>
                  ) : null}
                  {/* A source that failed or hit the deadline says so and offers
                      a retry; before 2026-09-06 it rendered as "No agents match". */}
                  {q.trim() && !searching && failure ? (
                    <div className={`px-3 ${myList.length || web.length ? 'py-2' : 'py-4'} text-center text-xs text-muted-foreground`}>
                      <p>{failure}</p>
                      <button onClick={() => setAttempt((n) => n + 1)} className="mt-1.5 text-xs font-medium text-foreground hover:underline">
                        Try again
                      </button>
                    </div>
                  ) : null}
                  {searching ? <p className="px-3 py-2 text-center text-xs text-muted-foreground">Searching…</p> : null}
                </div>
                {spawnFailed ? (
                  <p className="px-3 pb-1 pt-2 text-center text-xs text-[color:var(--bad,#9e3535)]">
                    That agent could not be put on the canvas just now. Try again.
                  </p>
                ) : null}
                <button onClick={() => setAgentPick(false)} className="mt-1 w-full pb-1 text-center text-xs text-muted-foreground hover:text-foreground">
                  {t('workspace.chooseDifferentEnv')}
                </button>
              </div>
            );
          })()
        ) : !panel ? (
          <div className="p-2">
            <p className="px-3 pb-1.5 pt-1 text-xs text-muted-foreground">
              {t('workspace.driveWith.intro')}
            </p>
            {CODING_ENVS.filter((env) => env.id !== 'robutler' || !!onRobutler).map((env) => (
              <button
                key={env.id}
                onClick={() => void pick(env)}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-accent"
              >
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${env.chip}`}>
                  {env.logo}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{env.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">{ENV_SUBS[env.id]}</span>
                </span>
                {env.id !== 'robutler' ? (
                  <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                ) : null}
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-3 p-4">
            <p className="text-xs text-muted-foreground">
              Opening <span className="font-medium text-foreground">{panel.env.name}</span>…
              {copied ? ' The prompt is in your clipboard — if it opened without it, just paste.' : ''}{' '}
              Keep this tab open: commands route to your live canvas.
            </p>
            <pre className="max-h-44 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-border bg-muted/40 p-3 text-[11px] leading-relaxed text-foreground">
              {panel.prompt}
            </pre>
            <div className="flex gap-2">
              <button
                onClick={() => void copyPrompt()}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium hover:bg-accent"
              >
                {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
                {copied ? t('settings.copied') : t('workspace.createWith.copyPrompt')}
              </button>
              <button
                onClick={() => launch(panel.env, panel.prompt)}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-foreground px-3 py-2 text-xs font-semibold text-background hover:opacity-90"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Open {panel.env.name}
              </button>
            </div>
            <button onClick={() => setPanel(null)} className="w-full text-center text-xs text-muted-foreground hover:text-foreground">
              {t('workspace.chooseDifferentEnv')}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
